"use client";

import Link from "next/link";
import { Suspense, useEffect, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "src/components/ui/dropdown-menu";
import { Button } from "../ui/button";
import MobileNavbar from "./navbar/MobileNavbar";
import SearchInputForLargeScreen from "./navbar/search-input-large-screen/SearchInput";
import CategoryNavLinks from "./navbar/CategoryNavLinks";
import getAuthToken from "src/utils/getAuthToken";
import { useRouter } from "next/navigation";
import getUserId from "src/utils/getUserId";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/contexts/LanguageContext";
import { Plus, User } from "lucide-react";
import getFormattedName from "src/utils/getFormatedUserName";
import CategoryNavLinkMobile from "./navbar/CategoryNavLinkMobile";
import { useSessionUser } from "src/hooks/useSession";
import NavButtons from "./navbar/NavButtons";

const Navbar = () => {
  const token = getAuthToken();
  const [user, setUser] = useState<any>(null);
  const [searchType, setSearchType] = useState("catalogue");
  const router = useRouter();
  const userId = getUserId();
  const { t, ready } = useTranslation();
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const { getUserData, clearUserData } = useSessionUser();
  const [isClient, setIsClient] = useState(false);


  useEffect(() => {
    if (userId) {
      // If no token is present, return early
      if (!token) return;
      const data = getUserData();
      setUser(data);
    }
  }, [userId]);


  useEffect(() => {
    // This only runs on the client
    setIsClient(true);
  }, []);

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedLanguage = e.target.value;
    setLanguage(selectedLanguage);
  };

  const handleLogout = () => {
    sessionStorage.clear();
    setIsOpen(false);
    clearUserData();
    router.replace("/");
  };

  const dropdownItems = [
    {
      label: "Account",
      type: "label",
    },
    {
      label: "Profile",
      href: "/settings/account/my-profile",
    },
    {
      label: "Orders",
      href: userId
        ? `/settings/account/my-orders?_id=${userId}&section=brought&status=All`
        : "#",
    },
    {
      label: "My Wallet",
      href: "/settings/account/my-wallet/wallet",
    },
    {
      label: "Account Settings",
      href: "/settings/account-settings/holiday-mode",
    },
    {
      label: "Personalization",
      href: "/settings/account/personalization",
    },
    {
      label: "Other Settings",
      type: "label",
    },
    {
      label: "Settings",
      href: "/settings/other-settings/dark-mode",
    },
    {
      label: "Logout",
      onClick: handleLogout,
    },
  ];

  return (
    <nav className="flex h-auto w-full flex-col dark:border-b-0 dark:bg-tertiary">
      <nav className="hidden h-auto w-full px-2 max-md:max-w-full md:flex md:flex-col md:px-10 max-w-[1440px] mx-auto">
        <nav className="flex h-[52px] w-full max-w-full items-center justify-between self-center py-10">
          <div className="flex items-center justify-between w-full">
            <Link href="/" prefetch={true}>
              <img
                src="/images/assets/checkmed-logo.png"
                alt="Company logo"
                className="cursor-pointer w-[150px]"
              />
            </Link>
            <Suspense fallback={<span>Loading...</span>}>
              <div className="flex h-10 items-center justify-start text-sm gap-x-2 rounded-[80px] border border-black border-opacity-30 bg-transparent text-black dark:bg-quaternary dark:text-white md:w-[460px]">
                <select
                  className="h-full gap-4 overflow-hidden text-sm rounded-[80px] border-none bg-transparent px-2 ring-offset-[#7F7F7F] placeholder:text-zinc-500 focus:outline-none focus:ring-0 focus:ring-[#7F7F7F] focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 dark:border-0 dark:bg-quaternary dark:ring-offset-quaternary dark:placeholder:text-[#7F7F7F] dark:focus:ring-secondary md:flex [&>span]:line-clamp-1"
                  onChange={(e) => setSearchType(e.target.value)}
                >
                  <option value="catalogue">{t("catalogue")}</option>
                  <option value="members">{t("members")}</option>
                </select>
                <div className="h-[20px] border-[1px] border-black border-opacity-30" />
                <SearchInputForLargeScreen searchType={searchType} />
              </div>
            </Suspense>
            {isClient && token && (
              <>
                <NavButtons />
                <Button
                  className="flex items-center justify-center gap-x-1 overflow-hidden rounded-[80px] bg-transparent px-5 text-center font-bold text-primary hover:bg-primary hover:bg-opacity-10 max-md:px-5 md:flex"
                  onClick={() => router.push("/sell-section")}
                >
                  <Plus className="font-bold text-primary" /> {t("sell_item")}
                </Button>
              </>
            )}

            <div className="flex items-center gap-x-2">
              {token ? (
                <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                  <DropdownMenuTrigger className="flex h-full items-center justify-center gap-x-2 font-bold text-black focus:bg-transparent data-[state=open]:bg-transparent dark:text-white dark:focus:bg-transparent dark:data-[state=open]:bg-transparent md:min-w-[142px]">
                    <div className="flex items-center justify-center gap-x-2">
                      {user?.profile_img_url && user?.profile_img_url !== "" ? (
                        <img
                          loading="lazy"
                          src={user?.profile_img_url}
                          alt={
                            user?.first_name?.charAt(0) +
                            user?.last_name?.charAt(0)
                          }
                          className="my-auto aspect-square w-11 shrink-0 rounded-[80px] object-cover object-center"
                        />
                      ) : (
                        <span className="my-auto flex size-8 items-center justify-center rounded-[80px] border border-zinc-500 object-cover object-center">
                          <User />
                        </span>
                      )}
                      <p className="line-clamp-1 text-base truncate">
                        {getFormattedName(user?.first_name, user?.last_name)}
                      </p>
                    </div>
                    <img
                      loading="lazy"
                      src="https://cdn.builder.io/api/v1/image/assets/TEMP/c4b2142076da6d88eabbcd348c6c814374b30d1ac81a1f9edbcc6abb1ef5394c?placeholderIfAbsent=true&apiKey=e597e4c45b0c4799aed7ecec6c3f8c22"
                      alt=""
                      className="my-auto aspect-[1.67] w-2.5 shrink-0 stroke-black stroke-[1.5px] object-contain"
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-48 space-y-2">
                    {dropdownItems.map((item, index) =>
                      item.type === "label" ? (
                        <DropdownMenuLabel
                          key={index}
                          className="text-sm text-gray-600"
                        >
                          {item.label}
                        </DropdownMenuLabel>
                      ) : (
                        <DropdownMenuItem
                          key={index}
                          onClick={
                            item.onClick ? item.onClick : () => setIsOpen(false)
                          }
                        >
                          {item.href ? (
                            <Link href={item.href}>{item.label}</Link>
                          ) : (
                            item.label
                          )}
                        </DropdownMenuItem>
                      ),
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link
                    href="/auth/login"
                    className="rounded-[80px] bg-primary hover:bg-opacity-90 px-5 py-2 text-center text-white"
                  >
                    {ready ? t("login") : "login"}
                  </Link>
                  <Link
                    href="/auth/login"
                    className="rounded-[80px] px-5 py-2 text-center bg-white text-primary border border-primary"
                  >
                    {ready ? t("sign_up") : "signup"}
                  </Link>
                </>
              )}
              <select
                // className="flex h-full w-[92px] gap-4 overflow-hidden rounded-3xl border-2 px-2 py-2 ring-offset-black placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-black focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white dark:bg-tertiary dark:text-white dark:ring-offset-zinc-950 dark:placeholder:text-white dark:focus:ring-black [&>span]:line-clamp-1"
                className="flex w-auto gap-4 overflow-hidden rounded-3xl px-2 py-2 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-black focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white dark:bg-tertiary dark:text-white dark:ring-offset-zinc-950 dark:placeholder:text-white dark:focus:ring-black [&>span]:line-clamp-1"
                value={language}
                onChange={handleLanguageChange}
              >
                <option value="en">Eng</option>
                <option value="fr">Fra</option>
                <option value="ar">Ara</option>
              </select>
            </div>
          </div>
        </nav>
        {/* Category in  Navbar */}
        <CategoryNavLinks />
      </nav>
      {/* Mobile Navbar */}
      <MobileNavbar />
      {/* Category NavLink Mobile  */}
      <CategoryNavLinkMobile />
    </nav>
  );
};

export default Navbar;
